import { useCallback, useEffect, useMemo, useState } from 'react'

import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { SupplyCategory } from '@ebalance/proto/bfftypes/v1/supply_category_pb'
import { useTranslation } from 'react-i18next'
import { useBlocker, useSearchParams } from 'react-router'

import {
  Alert,
  Box,
  Flex,
  HStack,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'
import { FaArrowDownToBracketRegular } from '@enechain/ecloud-icons'

import { CompactAlert } from '~/components/common/CompactAlert'
import { ConfirmDialog } from '~/components/common/ConfirmDialog'
import { CsvImportAppliedAlert } from '~/components/common/CsvUpload/CsvImportAppliedAlert'
import { DismissibleInfoAlert } from '~/components/common/DismissibleInfoAlert'
import { PanelResizer } from '~/components/common/PanelResizer'
import { RegionLoading } from '~/components/common/RegionLoading'
import { TradeSideLabel } from '~/components/common/TradeSideLabel'
import { WorkflowPageLayout } from '~/components/common/WorkflowPageLayout'
import { iconSize } from '~/constants/iconSize'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { useCurrentWorkflowCase } from '~/hooks/workflowCase/useCurrentWorkflowCase'
import { downloadCsv } from '~/libs/downloadCsv'
import { convertAreaLabel } from '~/models/area'
import {
  formatDateDisplay,
  formatDateTimeFull,
  toProtoDate,
} from '~/models/dateTime'
import { gateCloseFrontierFromProto } from '~/models/gateClose'
import { komaIndexToTime } from '~/models/komaToTime'
import { buildSupply48DataCsv } from '~/models/supply48DataCsv'
import { useGetPersonalSettingsQuery } from '~/pages/Settings/hooks/useGetPersonalSettingsQuery'
import { FilterSidebar } from '~/pages/Supply48Data/components/FilterSidebar'
import { NoDataGuide } from '~/pages/Supply48Data/components/NoDataGuide'
import { OverContractCellList } from '~/pages/Supply48Data/components/OverContractCellList'
import { Supply48DataChart } from '~/pages/Supply48Data/components/Supply48DataChart'
import { Supply48DataGrid } from '~/pages/Supply48Data/components/Supply48DataGrid'
import { Supply48DataToolbar } from '~/pages/Supply48Data/components/Toolbar'
import { useListSupply48Data } from '~/pages/Supply48Data/hooks/useListSupply48Data'
import {
  CHART_MAX_WIDTH,
  CHART_MIN_WIDTH,
  CHART_RESIZER_MIN_VIEWPORT,
  CHART_RESIZE_STEP,
  useResizableChartWidth,
} from '~/pages/Supply48Data/hooks/useResizableChartWidth'
import {
  InactiveKomaConfirmError,
  OverContractError,
  useSupply48EditSession,
} from '~/pages/Supply48Data/hooks/useSupply48EditSession'
import { useSupply48Filters } from '~/pages/Supply48Data/hooks/useSupply48Filters'
import { useSupply48ScrollHighlight } from '~/pages/Supply48Data/hooks/useSupply48ScrollHighlight'
import type {
  CellInputRejection,
  CellInputRejectionReport,
  SaveErrorReason,
} from '~/pages/Supply48Data/models/cellValue'
import { rejectionMessageKey } from '~/pages/Supply48Data/models/cellValue'
import { getConfirmDialogProps } from '~/pages/Supply48Data/models/confirmDialog'
import type { PendingConfirm } from '~/pages/Supply48Data/models/confirmDialog'
import { resolveCsvDownloadState } from '~/pages/Supply48Data/models/csvDownloadState'
import { protoDateToShortLabel } from '~/pages/Supply48Data/models/dateUtils'
import {
  displayUnitLabelKey,
  toDisplayValue,
} from '~/pages/Supply48Data/models/displayUnit'
import type { UnsavableCellEntry } from '~/pages/Supply48Data/models/editState'
import {
  collectSaveErrorCells,
  collectUnsavableCells,
} from '~/pages/Supply48Data/models/editState'
import {
  buildDateRange,
  countUnsavedKomas,
} from '~/pages/Supply48Data/models/gridRows'
import {
  findInputMissingSources,
  nonStandardLegIds,
} from '~/pages/Supply48Data/models/inputMissing'
import { countManualEdits } from '~/pages/Supply48Data/models/manualEditCount'
import {
  latestUpdatedAt,
  screenCsvFileName,
  toScreenCsvExportRows,
} from '~/pages/Supply48Data/models/screenCsvExport'
import { sourceDisplayLabel } from '~/pages/Supply48Data/models/sourceDisplayLabel'
import { isEditableSource } from '~/pages/Supply48Data/models/sourceKind'
import {
  parseSupply48InitialFilters,
  parseSupply48ScrollTarget,
} from '~/pages/Supply48Data/models/supply48QueryParams'

/**
 * 48点データページ。
 *
 * 機能スコープ:
 *   - 売り/買いセグメント + カテゴリタブ
 *   - フィルタサイドバー (売買 / 区分 / エリア / 対象日)
 *   - Excel ライクなグリッド (GC ロック / 手動補正 / 契約値超過)
 *   - 保存 (dirty 行のみ) / 破棄。AC-S4D-003 で編集モードトグルは廃止し、
 *     ロックされていないセルは常時直接編集できる (需要想定画面と同一の操作体系)
 *   - 表示単位トグル kWh/30min ⇄ kW (AC-S4D-037/038、永続値は kWh/30min 固定)
 *   - PendingEditsGuard (未保存編集での遷移確認)
 *   - §6 ConfirmDialog 統合 (6 ダイアログ → 単一 pendingConfirm state)
 *   - グラフパネル (Recharts BarChart)
 *   - §8 chartTargetLegId (string | null) で leg 変化時の誤表示を防止
 */

/**
 * CSVダウンロード確認ダイアログの「区分」 表示。
 * `Supply48DataGrid` の `categoryLabelKey` と同じ写像 (あちらは module private)。
 * 統合すると Grid の描画都合と ダイアログの文言都合が同じ関数に相乗りするので、
 * 写像だけを写して独立させる (キーは同じ i18n を引くので文言はズレない)。
 */
function csvCategoryLabelKey(category: SupplyCategory): string {
  switch (category) {
    case SupplyCategory.MARKET:
      return 'page:supply48Data.filter.categoryMarket'
    case SupplyCategory.FUTURES:
      return 'page:supply48Data.filter.categoryFutures'
    default:
      return 'page:supply48Data.filter.categoryBilateral'
  }
}

export const Supply48DataPage = (): React.ReactElement => {
  const { i18n, t } = useTranslation()
  const showSuccessToast = useSuccessToast()

  const [searchParams] = useSearchParams()
  /*
   * caseId 付きで開いている間は「読み込み完了かつ非 readonly」のときだけ書き込み
   * 可とする (= 取得中・取得失敗は readonly に倒す)。ケース状態が不明なまま編集を
   * 許すと、完了済み / 削除済みケースでも編集・保存が通る (server 側 enforce は
   * #4517 で別途追跡)。caseId 無しの通常利用は従来どおり編集可 (fail-closed の
   * 詳細な理由は useCurrentWorkflowCase の JSDoc 参照)。
   */
  const {
    isReadonly,
    isDateLocked,
    lockedTargetDate,
    isTargetDateReady,
    isTargetDateUnavailable,
  } = useCurrentWorkflowCase()

  const [initialFilters] = useState(() =>
    parseSupply48InitialFilters(searchParams),
  )
  /**
   * AC-S4D-052: 相対取引契約管理からの遷移直後、自動横スクロール・一時ハイライトの
   * 対象契約識別子 (取引ID)。mount 時 1 度だけ読む (initialFilters と同じ理由)。
   */
  const [scrollTargetTradeId] = useState(() =>
    parseSupply48ScrollTarget(searchParams),
  )

  // ------------- グラフパネル -------------
  /*
   * グラフ表示は**既定 ON** (デザイナー指摘 2026-08-27「グラフ表示をデフォルト ON に
   * する (8080準拠)」)。 proto `supply/48data.html` も `var showChart = true;`
   * (同 L647「既定 ON (2026-07-31 ユーザー指示)」) で、 app だけ OFF だった。
   * 対象 leg 未選択のままだとパネルが出ないが、 下の effect が先頭 leg を
   * 自動選択するので、 データ到着後に必ず先頭列のグラフが出る。
   */
  const [showChart, setShowChart] = useState(true)
  // グラフパネル幅 (PanelResizer で可変。 localStorage に永続化)
  const {
    width: chartWidth,
    changeWidth: changeChartWidth,
    resetWidth: resetChartWidth,
  } = useResizableChartWidth()

  // §8: chartTargetLegId (string | null) に変更
  const [chartTargetLegId, setChartTargetLegId] = useState<string | null>(null)

  // 表示単位 (AC-S4D-037: 既定 kWh/30min、永続値は不変) は他のフィルタと同じく
  // useSupply48Filters が保持する (AC-SC-031)。

  /*
   * AC-S4D-041: 採用できなかったセル入力を silent に捨てない。
   * グリッドは 1 操作ごとに拒否一覧 (拒否ゼロなら空配列) を返すため、
   * 正しい値を入れ直せばエラー表示はそのまま解除される。
   */
  const [rejectionReport, setRejectionReport] =
    useState<CellInputRejectionReport>({ kind: 'cells', rejections: [] })
  /*
   * AC-S4D-049: 「エラー（保存がバリデーションで拒否された）」セルの印
   * (GridCell の赤バー・outline・tooltip)。 rejectionReport (バナー) とは
   * 別に「直前の保存試行」を状態源にする (collectSaveErrorCells の docstring
   * 参照)。 消すのは保存試行 (handleSave の再計算)・保存成功・編集の一括破棄
   * (revertAll / フィルタ変更 / 編集破棄) だけで、 rejectionReport のような
   * 「1 打鍵ごと」の消去はしない (下の clearRejectionBanner 参照。ここを
   * clearRejections と一緒くたにすると、 保存拒否後に別セルへ 1 文字打つだけで
   * 拒否されたセルの印まで消え、 AC-S4D-049 が防ごうとしている「保存失敗が
   * 編集中と同じ見た目に戻り誤認する」状態を作ってしまう)。
   */
  const [saveErrorCells, setSaveErrorCells] = useState<
    Map<string, SaveErrorReason>
  >(new Map())
  /** rejectionReport (入力バナー) だけを消す。 1 打鍵ごとに呼ばれる経路専用。 */
  const clearRejectionBanner = useCallback(() => {
    setRejectionReport((prev) =>
      prev.kind === 'cells' && prev.rejections.length === 0
        ? prev
        : { kind: 'cells', rejections: [] },
    )
  }, [])
  /** バナーとセル単位のエラー印を両方消す。 保存試行・保存成功・編集の一括破棄で使う。 */
  const clearRejections = useCallback(() => {
    clearRejectionBanner()
    setSaveErrorCells((prev) => (prev.size === 0 ? prev : new Map()))
  }, [clearRejectionBanner])
  const handleCellInputRejections = useCallback(
    (report: CellInputRejectionReport) => {
      if (report.kind === 'cells' && report.rejections.length === 0) {
        clearRejectionBanner()
        return
      }
      setRejectionReport(report)
    },
    [clearRejectionBanner],
  )

  // ------------- §6 ConfirmDialog 統合 -------------
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm>(null)

  /**
   * CSVダウンロード確認モーダル (AC-S4D-007 / 007a)。
   *
   * §6 の `pendingConfirm` 統合には載せない: あちらは「編集を捨てる系」 の
   * 破壊的確認を i18n 文字列 body で捌く仕組みで、 こちらは (a) body が
   * フィルタ条件の表 = JSX、 (b) 破壊的でない出力操作、 と性質が違う。
   * 先例として blocker / save の確認も独立した ConfirmDialog を並べている。
   */
  const [isCsvDownloadOpen, setCsvDownloadOpen] = useState(false)

  // ------------- 個人設定 (エリア選択) -------------
  const { data: personalSettings, isLoading: isLoadingSettings } =
    useGetPersonalSettingsQuery()
  // §8: area fallback 廃止 (ロード完了まで UNSPECIFIED のまま)
  const availableAreas = useMemo(
    () => personalSettings?.settings?.businessAreas ?? [],
    [personalSettings],
  )

  // ------------- フィルタ hook -------------
  const {
    buySell,
    category,
    selectedArea,
    targetDate: filterTargetDate,
    protoTargetDate: filterProtoTargetDate,
    handleBuySellChange,
    handleCategoryChange,
    handleAreaChange,
    handleTargetDateChange,
    displayUnit,
    handleDisplayUnitChange,
  } = useSupply48Filters({
    availableAreas,
    hasDirtyRows: false, // 初期値。hasDirtyRows は editSession から取得するが循環を避けるため 0 固定
    onClearEditState: () => {
      // clearEditState は editSession 初期化後に handleWithDirtyGuard 経由で呼ぶ
    },
    initialBuySell: initialFilters.buySell,
    initialArea: initialFilters.area,
    initialDate: initialFilters.date,
  })
  // AC-WFW-006a Decision 3: caseId 付きで開いている間はケース対象日を
  // `?date=`・localStorage 復元値より優先する (フィルタ hook の内部 state へは
  // 書き戻さない = 直接アクセス時の値を汚さない)。取得完了までは filterTargetDate
  // (既定 / `?date=` / localStorage 由来) をそのまま表示するが、日付操作 UI は
  // disabled のため実質操作不可 (Decision 1、fail-closed)。
  const targetDate = lockedTargetDate ?? filterTargetDate
  const protoTargetDate =
    lockedTargetDate !== undefined
      ? toProtoDate(lockedTargetDate)
      : filterProtoTargetDate

  /*
   * AC-S4D-032 (市場列は常時 read-only) / AC-S4D-001 (先物カテゴリは保有量確認の
   * みで編集不可、issue #5129) 選択中は、GC ロック案内 / Excel 操作案内のいずれも
   * 成立しない (AC-S4D-036 と同型の誤誘導防止: 「編集できるのはゲートクローズ前の
   * コマのみ」「貼付・一括入力できる」は全セル編集不可のこれらカテゴリで事実と
   * 異なる案内になる)。
   */
  const isReadOnlyCategory =
    category === SupplyCategory.MARKET || category === SupplyCategory.FUTURES

  // ------------- API (§8: personalSettings ロード完了まで enabled: false) -------------
  const {
    data,
    isLoading,
    error: listError,
  } = useListSupply48Data({
    buySell,
    category,
    areaCode: selectedArea,
    // AC-S4D-002: 対象日は単日固定 (from = to)
    dateFrom: protoTargetDate,
    dateTo: protoTargetDate,
    // §8: personalSettings ロード完了 + area が確定してから fetch する。
    // caseId 付きで開いている間はケース対象日が確定するまで取得しない
    // (AC-WFW-006a Decision 1: 既定日 / `?date=` での 1 日分 query を出さない)。
    enabled:
      !isLoadingSettings &&
      selectedArea !== AreaCode.UNSPECIFIED &&
      isTargetDateReady,
  })
  // 一覧取得時の GC 境界。セルロック・入力漏れ・手動変更件数の判定が同じ値を使う。
  const gateCloseFrontier = useMemo(
    () => gateCloseFrontierFromProto(data?.gateCloseFrontier),
    [data],
  )

  /*
   * AC-S4D-052: 遷移直後の自動横スクロール・一時ハイライト対象の解決。
   * グリッドはフィルタ変更 (対象日・エリア・売買・区分) で unmount → remount
   * するため、一度きりの判定は remount しないページ側 (= 遷移パラメータと
   * データの保持主体) で行う。グリッドは解決済みの legId を受けて DOM 操作
   * (scrollIntoView・ハイライト描画) だけを担う。
   */
  const { highlightedLegId: scrollHighlightedLegId } =
    useSupply48ScrollHighlight({
      scrollTargetTradeId,
      // 取得失敗は「対象なし」で判定を確定させる (後のフィルタ操作で遅れて発火させない)
      legs: listError ? [] : data?.sources,
    })

  // ------------- visibleDates (表示期間の SoT) -------------
  // AC-S4D-002: 対象日は単日固定のため、表示行は常に 1 日分 (from = to)。
  const visibleDates = useMemo(
    () => buildDateRange(targetDate, targetDate),
    [targetDate],
  )

  // ------------- 非定型の取引の列 (AC-S4D-060) -------------
  // 編集セッション・未保存数・grid が同じ集合を使う (差金決済タブの列は含めない)
  const nonStandardIds = useMemo(
    () => nonStandardLegIds(data?.sources ?? [], category),
    [data, category],
  )

  // ------------- 編集セッション hook -------------
  const {
    editState,
    dayIndex,
    hasDirtyRows,
    saveError,
    isSaving,
    applyCellEdits,
    undo,
    redo,
    canUndo,
    canRedo,
    handleSave: handleSaveRaw,
    doSave,
    doRevertLegToAuto,
    doRevertAllToAuto,
    clearEditState,
  } = useSupply48EditSession({
    data,
    visibleDates,
    category,
    nonStandardLegIds: nonStandardIds,
  })

  // フィルタ変更時の dirty guard は hook 内の withDirtyGuard ではなく、下方の
  // handleWithDirtyGuard (§6 pendingConfirm) が SoT (= hook は editSession より
  // 先に初期化されるため、hook へ渡した hasDirtyRows / onClearEditState は不使用)。

  // ------------- AC-S4D-060: 非定型の取引の入力漏れ -------------
  // 保存値で判定する (編集中の値は見ない)。保存・提出・CSV の操作は止めない。
  // 読み取り専用 (完了済みケース等) は値を入れられない = ロック済みの日なので出さない
  const inputMissingSources = useMemo(
    () =>
      isReadonly
        ? []
        : findInputMissingSources(
            data?.sources ?? [],
            category,
            visibleDates,
            dayIndex,
            gateCloseFrontier,
          ),
    [data, category, visibleDates, dayIndex, isReadonly, gateCloseFrontier],
  )
  const inputMissingLegIds = useMemo(
    () => new Set(inputMissingSources.map((s) => s.supply48SourceId)),
    [inputMissingSources],
  )

  // ------------- AC-S4D-008: リセット対象の手動変更件数 -------------
  // リセット操作の表示ゲート (1 件以上) と確認ダイアログの件数明示の両方がこれを使う。
  // 渡す legIds は編集可能 source (isEditableSource の allow-list) に絞る —
  // revertAll (doRevertAllToAuto) 自身も同じ allow-list で非編集 source を
  // skip するため、絞らずに全 source id を渡すと
  // manualEditCount.ts の不変条件「数えた件数 = 戻る件数」が崩れる
  // (= 市場行のような非編集 source を数に含めてしまう窓ができる)。
  // AC-S4D-001 (issue #5129): 先物カテゴリ選択中は legIds を空にする — 先物
  // legは isEditableSource だけでは弾けない (CONTRACT source のため) ので、
  // 件数側でも 0 に固定してツールバーの「全て自動計算に戻す」が出ないようにする
  // (isReadOnlyCategory と同じ理由、Supply48DataGrid の isFuturesCategory と対称)。
  const manualEditCounts = useMemo(
    () =>
      countManualEdits({
        legIds:
          category === SupplyCategory.FUTURES
            ? []
            : (data?.sources
                .filter((s) => isEditableSource(s))
                .map((s) => s.supply48SourceId) ?? []),
        visibleDates,
        dayIndex,
        editState,
        gateClose: gateCloseFrontier,
      }),
    [data, visibleDates, dayIndex, editState, category, gateCloseFrontier],
  )

  /**
   * CSV ダウンロード確認 (AC-S4D-007) の「反映されません」件数。
   * manualEditCounts とは別の量なので分けて持つ (countUnsavedKomas の docstring)。
   */
  const unsavedKomaCount = useMemo(
    () => countUnsavedKomas(editState, dayIndex, nonStandardIds),
    [editState, dayIndex, nonStandardIds],
  )

  // ------------- ConfirmDialog: 統合ハンドラ -------------

  const handleRevertLegToAuto = useCallback(
    (legId: string) => {
      setPendingConfirm({
        kind: 'revertLeg',
        run: () => doRevertLegToAuto(legId),
        manualEditCount: manualEditCounts.byLeg.get(legId) ?? 0,
        // AC-S4D-008: {対象} は列ヘッダーに出す名前
        target: sourceDisplayLabel(
          data?.sources.find((s) => s.supply48SourceId === legId),
          t,
        ),
      })
    },
    [doRevertLegToAuto, manualEditCounts, data, t],
  )

  const handleRevertAll = useCallback(() => {
    setPendingConfirm({
      kind: 'revertAll',
      /*
       * 編集内容を一括で捨てる時は拒否一覧も一緒に捨てる。 編集状態と寿命を
       * 揃えないと、 もう存在しない値についてのエラーがバナーに残り続ける。
       * この掃除は「変更を破棄」 が担っていたが、 8080 に合わせて撤去したので
       * 唯一の一括経路であるこちらへ移した。 列単位の `revertLeg` は他列の
       * 拒否まで消してしまうので対象外。
       */
      run: () => {
        doRevertAllToAuto()
        clearRejections()
      },
      manualEditCount: manualEditCounts.total,
    })
  }, [doRevertAllToAuto, clearRejections, manualEditCounts])

  /*
   * AC-S4D-028: 保存単位 (kWh/30min) が整数にならない値 (= kW 表示での奇数入力) は
   * 保存を拒否して理由を出す。表示単位のまま 1 打鍵ごとに偶数判定すると「102」の
   * 途中経過「1」が弾かれて入力が壊れるため、確定値を見る保存時に判定する。
   * AC-S4D-047: 換算後の値が OCCTO 提出値の桁数上限 (9 桁 = 999,999,999
   * kWh/30min) を超える値も同じ保存時チェックで拒否する
   * (`collectUnsavableCells` が理由を返し分ける)。
   *
   * [レビュー指摘 #9031] 判定そのもの (`checkSavableStoredValue` の呼び出しと
   * dirty行×定義済みslotの走査) は `editState.collectUnsavableCells` に一本化した。
   * ここはその結果 (`UnsavableCellEntry[]`) をバナー表示用のラベル付き形式
   * (`CellInputRejection`) へ写すだけの純関数にし、 セル単位のエラー印
   * (`collectSaveErrorCells`) と同じ集合から派生させる (handleSave 参照。
   * 条件を変えた時にバナー件数とセル印がずれるのを防ぐ)。
   */
  const toUnsavableRejections = useCallback(
    (entries: readonly UnsavableCellEntry[]): CellInputRejection[] =>
      entries.map((entry) => {
        const leg = data?.sources.find(
          (s) => s.supply48SourceId === entry.legId,
        )
        return {
          reason: entry.reason,
          /*
           * oddInKw は保存単位が非整数になる原因が常に kW 表示での奇数入力
           * なので、問題の値は常に kW 換算値で示す (現在の表示単位のまま
           * 出すと、kW で 101 を入れた後 kWh/30min へ戻して保存した時に
           * 「値 50.5 kW は奇数です」という二重に誤った案内になる)。
           * overDigitLimit (AC-S4D-047) 等それ以外の拒否理由は、現在の
           * 表示単位のまま示す (メッセージの {{unit}} と一致させる)。
           */
          raw: String(
            toDisplayValue(
              entry.stored,
              entry.reason === 'oddInKw' ? 'kW' : displayUnit,
            ) ?? '',
          ),
          itemName: sourceDisplayLabel(leg, t),
          dateLabel: protoDateToShortLabel(entry.deliveryDate),
          komaLabel: komaIndexToTime(entry.komaIdx),
        }
      }),
    [data, t, displayUnit],
  )

  /**
   * AC-S4D-045 の確認ダイアログを積む共通処理。 handleSave の初回失敗経路と
   * confirmedSave 内部の catch (AC-S4D-018 確認後の再送で追加の時間帯区分違反が
   * 見つかった場合) の 2 箇所から呼ぶため、pendingConfirm の組み立てをここに
   * 1 本化する (2026-08-22 レビュー指摘: 同じ setPendingConfirm 配線が複製
   * されていた)。
   */
  const openInactiveKomaConfirm = useCallback(
    (message: string, run: () => void) => {
      setPendingConfirm({ kind: 'inactiveKoma', run, message })
    },
    [],
  )

  /**
   * doSave を実行し、 AC-S4D-045 (時間帯区分整合) の警告が返ってきたら確認
   * ダイアログを積む (確認後は confirmInactiveKoma=true で自分自身を再実行)。
   * AC-S4D-018 (契約量超過) 確認後の再保存 (下の handleSave の overContract
   * 分岐) もこの経路を通す — 1 回の保存で両方の警告が出るケース (契約量超過を
   * 確認した後の再送で時間帯区分整合にも引っかかる) を取りこぼさないため。
   */
  const confirmedSave = useCallback(
    async function runConfirmedSave(
      confirmInactiveKoma?: boolean,
    ): Promise<void> {
      try {
        // **resolve = 成功ではない**。 doSave は Aborted / InvalidArgument 等を
        // 内部で握って saveError に出すだけなので、 戻り値で判定しないと
        // エラーバナーと成功 toast が同時に出る (devin review 指摘 #6759)。
        const saved = await doSave(confirmInactiveKoma)
        if (!saved) return
        clearRejections()
        // 2026-08-28 デザイナー指摘「削除・追加・編集の成功時は全画面共通で toast」。
        // 確認ダイアログを経た再保存の経路 (こちら) と 初回保存の経路
        // (handleSave) はどちらか一方しか成功しないので二重に出ない。
        showSuccessToast({ title: t('page:supply48Data.saveSuccessToast') })
      } catch (err) {
        if (err instanceof InactiveKomaConfirmError) {
          openInactiveKomaConfirm(
            err.message,
            () => void runConfirmedSave(true),
          )
        }
      }
    },
    // t / showSuccessToast を依存に入れる: 画面表示後に言語を切り替えてから
    // 確認を経て保存すると、 切替前の言語で toast が出る (codex review 指摘 #6759)。
    [doSave, clearRejections, openInactiveKomaConfirm, showSuccessToast, t],
  )

  // ------------- CSVダウンロード (AC-S4D-007 / 007a) -------------
  /**
   * 出力行。 **画面が表示している (= backend が売買 / 区分 / エリア / 対象日で
   * 絞り込んだ) 応答から作る**。 全件取得の RPC は無く (撤去済み)、
   * 画面のフィルタ状態を反映した CSV にするため (`models/screenCsvExport` の godoc 参照)。
   * 読むのはサーバ値だけで、 未保存の編集 (editState) は含めない。
   */
  const csvExportRows = useMemo(
    () => toScreenCsvExportRows(data?.sources ?? []),
    [data?.sources],
  )

  /** 出力する行の中で最も新しい更新時刻 (AC-S4D-007 の「基準時点」)。 */
  const csvUpdatedAt = useMemo(
    () => latestUpdatedAt(data?.sources ?? []),
    [data?.sources],
  )

  /**
   * 一覧が未取得 / 取得失敗の間はダウンロードさせない。
   *
   * 応答が無い時に空配列へフォールバックすると、 **実データがあるのに
   * ヘッダーのみの CSV** が落ちる。 0 件を許すのは「取得できた結果が 0 件」
   * の時だけ (AC-S4D-007-1)。
   */
  const canDownloadCsv = data !== undefined && listError === null

  /** 押せない理由を tooltip に出すための状態 (2026-09-14 デザイナー指摘)。 */
  const csvDownloadState = resolveCsvDownloadState({
    isLoaded: data !== undefined,
    // ケース対象日を得られない間は一覧 query が止まったままで、待っても読み込みは終わらない。
    hasError: listError !== null || isTargetDateUnavailable,
  })

  const handleCsvDownload = useCallback(() => {
    if (!canDownloadCsv) return
    downloadCsv(
      screenCsvFileName({
        from: targetDate,
        to: targetDate,
        isEnglish: i18n.language.startsWith('en'),
      }),
      // 表示言語が英語ならヘッダー行も英語にする (spec の英語ヘッダー対応)。
      buildSupply48DataCsv(csvExportRows, {
        isEnglish: i18n.language.startsWith('en'),
      }),
    )
    setCsvDownloadOpen(false)
  }, [canDownloadCsv, csvExportRows, targetDate, i18n.language])

  // ------------- handleSave with ConfirmDialog -------------
  const handleSave = useCallback(async (): Promise<void> => {
    // AC-S4D-049 / レビュー指摘 #9031: 保存を拒否されるコマの走査は
    // `collectUnsavableCells` に一本化し、 バナー (unsavable) とセル単位の
    // エラー印 (saveErrorCells) を同じ結果から写す (二重走査・条件drift を防ぐ)。
    const unsavableEntries = collectUnsavableCells(editState)
    const unsavable = toUnsavableRejections(unsavableEntries)
    setSaveErrorCells(collectSaveErrorCells(unsavableEntries))
    if (unsavable.length > 0) {
      setRejectionReport({ kind: 'cells', rejections: unsavable })
      return
    }
    try {
      // confirmedSave と同じ理由で戻り値を見る (resolve = 成功ではない)。
      const saved = await handleSaveRaw()
      if (!saved) return
      // 保存できた = 拒否対象は残っていない
      clearRejections()
      showSuccessToast({ title: t('page:supply48Data.saveSuccessToast') })
    } catch (err) {
      if (err instanceof OverContractError) {
        setPendingConfirm({
          kind: 'overContract',
          cells: err.cells,
          run: () => void confirmedSave(),
        })
      } else if (err instanceof InactiveKomaConfirmError) {
        openInactiveKomaConfirm(err.message, () => void confirmedSave(true))
      }
    }
  }, [
    toUnsavableRejections,
    editState,
    handleSaveRaw,
    confirmedSave,
    clearRejections,
    openInactiveKomaConfirm,
    showSuccessToast,
    t,
  ])

  // ------------- PendingEditsGuard (AC-010) -------------
  // 編集モードトグル廃止 (AC-S4D-003) に伴い、未保存編集の有無だけで判定する
  const blocker = useBlocker(hasDirtyRows)
  const [showBlockerDialog, setShowBlockerDialog] = useState(false)
  useEffect(() => {
    if (blocker.state === 'blocked') {
      setShowBlockerDialog(true)
    }
  }, [blocker.state])

  // ------------- §6 ConfirmDialog: kind → title/body/destructive の表引き -------------
  const confirmDialogProps =
    pendingConfirm !== null ? getConfirmDialogProps(pendingConfirm, t) : null

  // ------------- §8: chartTargetLegId が legs に存在しない場合はパネルを閉じる -------------
  useEffect(() => {
    if (
      showChart &&
      chartTargetLegId !== null &&
      data !== undefined &&
      !data.sources.some((s) => s.supply48SourceId === chartTargetLegId)
    ) {
      setShowChart(false)
      setChartTargetLegId(null)
    }
  }, [data, showChart, chartTargetLegId])

  /*
   * グラフ表示 ON かつ対象列が未選択なら先頭 leg を自動選択する。
   * design/supply/48data.html は chartColIdx=0 既定でトグル ON 時に即
   * renderChart() する (= 先頭列のグラフが必ず出る)。パネル描画条件は
   * chartTargetLegId !== null なので、未選択のままだとトグル ON でも何も出ない。
   * ツールバーは一覧ロード前でも描画されるため、ハンドラではなく effect にして
   * 「ロード前に ON → データ到着」でも自己回復させる。
   */
  useEffect(() => {
    if (
      showChart &&
      chartTargetLegId === null &&
      data !== undefined &&
      data.sources.length > 0
    ) {
      setChartTargetLegId(data.sources[0].supply48SourceId)
    }
  }, [showChart, chartTargetLegId, data])

  // ------------- フィルタ変更 dirty guard (§6 統合版) -------------
  // useSupply48Filters の withDirtyGuard ではなく pendingConfirm に統合する
  const handleWithDirtyGuard = useCallback(
    (applyChange: () => void) => {
      // フィルタを跨いだ編集内容は保存対象にならないため、切り替えを確定した
      // 経路で編集状態と一緒に拒否一覧・エラー印も捨てる (キャンセルでは残す)
      if (hasDirtyRows) {
        setPendingConfirm({
          kind: 'filterChange',
          run: () => {
            applyChange()
            clearEditState()
            clearRejections()
          },
        })
      } else {
        applyChange()
        clearEditState()
        clearRejections()
      }
    },
    [hasDirtyRows, clearEditState, clearRejections],
  )

  const breadcrumbItems = [
    { label: t('page:supply48Data.breadcrumb.supply') },
    { label: t('page:supply48Data.breadcrumb.title'), bold: true },
  ]

  const formatRejection = (rejection: CellInputRejection): string =>
    t(rejectionMessageKey(rejection.reason), {
      date: rejection.dateLabel,
      item: rejection.itemName,
      koma: rejection.komaLabel,
      raw: rejection.raw,
      unit: t(displayUnitLabelKey(displayUnit)),
    })

  // ------------- レンダリング -------------
  return (
    <WorkflowPageLayout
      breadcrumbItems={breadcrumbItems}
      notices={
        <>
          {/* エラーバナー (業務が止まる: 先頭)。
              1 行で完結するので本文を title に渡す。
              ケース対象日を得られない時は一覧を取得しないので、取得失敗と同じく扱う。 */}
          {(saveError !== undefined ||
            listError !== null ||
            isTargetDateUnavailable) && (
            <CompactAlert
              role="alert"
              status="error"
              title={saveError ?? t('page:supply48Data.serverError.listFailed')}
            />
          )}

          {/* CSV を取り込んで戻った時の結果 (1 回だけ)。 エラーより優先度が低いので下に置く */}
          <CsvImportAppliedAlert kind="supply48Data" />
        </>
      }
    >
      {/*
        外周 padding を**持たない**。 `PageLayout` が包む DS の
        `SideMenuLayoutContentLayout` が `padding="4"` (16px) を既に持っており
        (design `css/layout.css` の `.content { padding: 16px }` と同値)、 ここで
        更に 16px を足すと本画面だけ上・左・右が 32px になる。
        デザイナー指摘 2026-08-28「ここの余白が他の画面と違う」 の原因。
      */}
      <Box>
        {/* メインレイアウト: フィルタサイドバー + グリッド */}
        <Flex alignItems="flex-start" gap={4}>
          {/* フィルタサイドバー */}
          <FilterSidebar
            availableAreas={availableAreas}
            buySell={buySell}
            category={category}
            displayUnit={displayUnit}
            isDateLocked={isDateLocked}
            isTargetDateConfirmed={lockedTargetDate !== undefined}
            onAreaChange={(v) =>
              handleWithDirtyGuard(() => handleAreaChange(v))
            }
            onBuySellChange={(v) =>
              handleWithDirtyGuard(() => handleBuySellChange(v))
            }
            onCategoryChange={(v) =>
              handleWithDirtyGuard(() => handleCategoryChange(v))
            }
            onDisplayUnitChange={handleDisplayUnitChange}
            onTargetDateChange={(v) =>
              handleWithDirtyGuard(() => handleTargetDateChange(v))
            }
            selectedArea={selectedArea}
            targetDate={targetDate}
          />

          {/* グリッドエリア。

              **上詰めの補正を入れないこと。** 左右とも `padding 12px + 枠 1px`
              の同じカードなので、箱の上端は `alignItems="flex-start"` で揃い、
              中身も同じ 13px 内側から始まる。

              [2026-09-02 修正] ここには `paddingTop="13px"` が入っていた。
              入れた時点 (`6d6b2d78aa`) の右カラムは**カードを持たず**中身が 0 から
              始まっていたので、サイドバーの内寸に合わせる補正として正しかった。
              その後 `f9ac5a4fed` でツールバーの器 (地・枠・角丸・内余白) が戻り
              右カラムも 13px の内寸を持ったため、**補正が二重計上になって
              ツールバーの箱だけ 13px 下がっていた** (デザイナー指摘 2026-09-02
              「48点データ > 段差」)。器が戻ったので補正は不要。

              つまりこの位置の不変条件は「**左右のカードの内寸が同じなら補正は要らない**」。
              片方の器を外す変更をする時は、ここもセットで見直すこと。 */}
          <Box flex={1} minWidth={0}>
            {/* Alert は業務上の優先度の順に並べる (UI 辞書 alert-placement.md 2)。
                保存を止める入力拒否 (AC-S4D-041 / 042) → 対応が要る入力漏れ (AC-S4D-060)
                → 知っておけばよい案内 (GC ロック・Excel 操作) の順。 */}
            {/*
              AC-S4D-042: 貼付は「数値以外が入力された N件のセルは反映していません」の件数通知。
              貼付は一度に数百セルを扱うので、セルごとの理由を並べても読めない。
            */}
            {rejectionReport.kind === 'pasteSkipped' && (
              <Box marginBottom={2}>
                <CompactAlert
                  role="alert"
                  status="error"
                  title={t('page:supply48Data.inputRejected.pasteSkipped', {
                    count: rejectionReport.count,
                  })}
                />
              </Box>
            )}

            {/*
              AC-S4D-041: 入力を受け付けられない理由を出す (黙って戻さない)。
              Compact Alert は見出し + 説明の 1 つの流れで、内訳の行を積まない (UI 辞書 alert-placement.md §0)。
              1 件目の理由を見出しにし、2 件目以降は件数の要約にする (各セルはエラーの見た目で残る。AC-S4D-049)。
            */}
            {rejectionReport.kind === 'cells' &&
              rejectionReport.rejections.length > 0 && (
                <Box marginBottom={2}>
                  <CompactAlert
                    description={
                      rejectionReport.rejections.length > 1
                        ? t('page:supply48Data.inputRejected.more', {
                            count: rejectionReport.rejections.length - 1,
                          })
                        : undefined
                    }
                    role="alert"
                    status="error"
                    title={formatRejection(rejectionReport.rejections[0])}
                  />
                </Box>
              )}

            {/* AC-S4D-060: 非定型の取引の入力漏れの注意 (対象の列見出しにも「⚠ 未入力」) */}
            {inputMissingSources.length > 0 && (
              // Compact Alert: 見出し + 説明 (本文と対象の列) を 1 つの流れに置く (行を積まない。alert-placement.md §0)
              <Box marginBottom={2}>
                <CompactAlert
                  description={`${t('page:supply48Data.inputMissing.description')} ${t(
                    'page:supply48Data.inputMissing.columns',
                    {
                      columns: inputMissingSources
                        .map((s) => sourceDisplayLabel(s, t))
                        .join(
                          t('page:supply48Data.inputMissing.columnSeparator'),
                        ),
                    },
                  )}`}
                  status="warning"
                  title={t('page:supply48Data.inputMissing.title')}
                />
              </Box>
            )}

            {/* GC ロック注意書き (市場・先物カテゴリは常時 read-only のため出さない) */}
            {!isReadOnlyCategory &&
              data !== undefined &&
              gateCloseFrontier !== undefined && (
                /* 凡例なので閉じられる (proto は info alert 全部に × を出す)。
                   閉じた状態は localStorage に残り、 文言が変われば再表示される。 */
                <DismissibleInfoAlert
                  marginBottom={2}
                  compact
                  storageKey="supply48Data.gcLockNote"
                  title={t('page:supply48Data.gcLockNote')}
                />
              )}

            {/*
              Excel ライクなキーボード操作の案内 (AC-S4D-003、操作体系の正本は
              docs/specs/conventions.md「Excelライクなグリッド編集」)。
              閲覧モードでは書き込み操作が効かないので出さない。市場・先物カテゴリ
              (AC-S4D-032 / AC-S4D-001、常時 read-only) も同じ理由で出さない。

              gcLockNote と同じく閉じられる。 proto (`design/js/nav.js` の injectDismiss) は
              `.ecl-alert--info` 全部に × を注入しており、 この alert も文章のみで
              除外条件 (modal 配下 / 操作部品を含む) に当たらない。 むしろ gcLockNote が
              条件付き表示なのに対し、 こちらは**編集モードなら常時表示**の 5 行相当の
              長文で表を押し下げるので、 閉じられる必要はこちらの方が高い
              (デザイナー指摘 2026-08-28)。
            */}
            {!isReadonly && !isReadOnlyCategory && (
              <DismissibleInfoAlert
                marginBottom={2}
                compact
                description={t('page:supply48Data.excelHintDescription')}
                storageKey="supply48Data.excelHint"
                title={t('page:supply48Data.excelHintTitle')}
              />
            )}

            {/*
             * §10 Toolbar コンポーネント。
             * design/supply/48data.html 準拠: フィルタサイドバーの右カラム内、
             * バナー群の下・テーブル直上に置く (ページ最上部の全幅ではない)。
             */}
            <Supply48DataToolbar
              canRedo={canRedo}
              canUndo={canUndo}
              csvDownloadState={csvDownloadState}
              hasDirtyRows={hasDirtyRows}
              isReadonly={isReadonly}
              isSaving={isSaving}
              manualEditCount={manualEditCounts.total}
              onCsvDownload={() => {
                setCsvDownloadOpen(true)
              }}
              onRedo={redo}
              onRevertAll={handleRevertAll}
              onSave={() => void handleSave()}
              onShowChartChange={setShowChart}
              onUndo={undo}
              showChart={showChart}
            />

            {/* ローディング (下のデータなし・グリッドは `!isLoading` で出さない)。
                個人設定の読み込み中もエリアが未確定 (UNSPECIFIED) なので、 その間に
                「エリアを選択してください」 を出さない (UI 辞書 region-loading.md:
                読み込み中に空の案内を出さない)。 */}
            {(isLoading || isLoadingSettings) && <RegionLoading isLoading />}

            {/* データなし */}
            {!isLoading &&
              !isLoadingSettings &&
              selectedArea === AreaCode.UNSPECIFIED && (
                <Box padding={8} textAlign="center">
                  <Text color="Text.midEmphasis" fontSize="sm">
                    {t('page:supply48Data.selectAreaHint')}
                  </Text>
                </Box>
              )}

            {/* データの入れ方の案内 (デザイナー指示 2026-08-31)。
                取引 (leg) が 1 件も無い時は、 グリッドを出しても**列の無い空の表**が
                並ぶだけで「どうすれば出るのか」 が画面から読めない。 行の主な出どころは
                相対取引契約からの自動生成なので、 フィルタだけを案内していた従来の
                1 行では足りない (`supply-48-generation.md`)。 */}
            {!isLoading &&
              data !== undefined &&
              selectedArea !== AreaCode.UNSPECIFIED &&
              data.sources.length === 0 && <NoDataGuide category={category} />}

            {/* グリッド + グラフパネル横並び */}
            {!isLoading &&
              data !== undefined &&
              selectedArea !== AreaCode.UNSPECIFIED &&
              data.sources.length > 0 && (
                <Flex
                  // グラフパネルを表と同じ高さまで伸ばす (デザイナー指摘 2026-08-28
                  // 「グラフの height をブラウザいっぱいに」)。 既定の flex-start だと
                  // 子の高さが内容依存になり、 パネル側の `height="100%"` が解決できず
                  // `auto` に落ちる (codex 指摘 #6652)。 8080 は `.s48-body` が横 flex +
                  // `.s48-chart-panel` が縦 flex で、 パネルは行の高さまで伸びる。
                  alignItems="stretch"
                  gap={3}
                >
                  <Box flex={1} minWidth={0}>
                    <Supply48DataGrid
                      category={category}
                      chartTargetLegId={showChart ? chartTargetLegId : null}
                      dayIndex={dayIndex}
                      displayUnit={displayUnit}
                      editState={editState}
                      gateClose={gateCloseFrontier}
                      inputMissingLegIds={inputMissingLegIds}
                      isReadonly={isReadonly}
                      legs={data.sources}
                      manualEditCountByLeg={manualEditCounts.byLeg}
                      nonStandardLegIds={nonStandardIds}
                      onCellEdits={applyCellEdits}
                      onCellInputRejections={handleCellInputRejections}
                      onLegHeaderClick={(legId) => {
                        setChartTargetLegId(legId)
                        setShowChart(true)
                      }}
                      onRedo={redo}
                      onRevertLegToAuto={handleRevertLegToAuto}
                      onUndo={undo}
                      saveErrorCells={saveErrorCells}
                      scrollHighlightedLegId={scrollHighlightedLegId}
                      visibleDates={visibleDates}
                    />
                  </Box>

                  {/* テーブル / グラフ間の幅可変ハンドル。
                      proto `supply/48data.html` の `.s48-resizer` (共通
                      `js/panel-resizer.js`、 需要想定と共有) と同じ形で、
                      **グラフ表示中かつ viewport ≥1100px のときだけ**出る
                      (表示条件の後段は PanelResizer 側の media query が持つ)。
                      可変なのは右のグラフパネルなので `invert`。 */}
                  {showChart && chartTargetLegId !== null && (
                    <PanelResizer
                      width={chartWidth}
                      invert
                      max={CHART_MAX_WIDTH}
                      min={CHART_MIN_WIDTH}
                      minViewportWidth={CHART_RESIZER_MIN_VIEWPORT}
                      onReset={resetChartWidth}
                      onWidthChange={changeChartWidth}
                      step={CHART_RESIZE_STEP}
                    />
                  )}

                  {/* グラフパネル (選択中の受渡日 = visibleDates[0] = dateFrom を描画) */}
                  {showChart && chartTargetLegId !== null && (
                    <Supply48DataChart
                      width={chartWidth}
                      dayIndex={dayIndex}
                      displayUnit={displayUnit}
                      editState={editState}
                      legs={data.sources}
                      onClose={() => setShowChart(false)}
                      selectedDate={visibleDates[0]}
                      targetLegId={chartTargetLegId}
                    />
                  )}
                </Flex>
              )}
          </Box>
        </Flex>

        {/* 未保存遷移確認ダイアログ (PendingEditsGuard) */}
        {/* CSVダウンロード確認 (AC-S4D-007)。 データ種別・適用中のフィルタ条件・
            該当件数・基準時点を出してから「ダウンロード開始」 で出力する。 */}
        <ConfirmDialog
          body={
            <VStack align="stretch" gap={3}>
              {hasDirtyRows && (
                <Alert
                  description={t(
                    'page:supply48Data.csvDownloadDialog.dirtyWarningDesc',
                    // 「CSV に載らないコマ数」。 manualEditCounts (= AC-S4D-008 の
                    // 「自動計算に戻す」 対象 = 保存後も残る手動変更コマ) とは別の量。
                    // 流用すると保存済みの手動編集まで「編集中」 として数える
                    // (PR #6420 レビュー指摘)。 countUnsavedKomas の docstring 参照。
                    { count: unsavedKomaCount },
                  )}
                  status="warning"
                  title={t(
                    'page:supply48Data.csvDownloadDialog.dirtyWarningTitle',
                  )}
                />
              )}
              {/* 0 件のときだけ「何が落ちるか」 を明示する (2026-08-26 デザイナー判断)。
                  spec `AC-S4D-007-1` は「該当行 0 件ならヘッダ行のみの CSV を生成する
                  (0 件エラーにはしない)」 と定めており、 ボタンも無効化しない
                  (= まだデータの無い受渡日に対して**空の雛形を落として書き込む**導線が
                  この機能の目的の一つ)。 ただし件数「0 件」 だけでは「空ファイルが落ちた」
                  と受け取られうるので、 意図した挙動であることを 1 行添える。 */}
              {csvExportRows.length === 0 && (
                <Alert
                  status="info"
                  title={t(
                    'page:supply48Data.csvDownloadDialog.emptyResultDesc',
                  )}
                />
              )}
              <VStack align="stretch" gap={1}>
                {(
                  [
                    [
                      'dataType',
                      t('page:supply48Data.csvDownloadDialog.dataTypeValue'),
                    ],
                    ['targetDate', formatDateDisplay(targetDate)],
                    ['area', convertAreaLabel(selectedArea)],
                    // 売/買 は共通 [[TradeSideLabel]] に委譲する
                    // (色・文言の写像を画面ごとに持たない)。
                    [
                      'buySell',
                      <TradeSideLabel key="buySell" as="span" side={buySell} />,
                    ],
                    ['category', t(csvCategoryLabelKey(category))],
                    [
                      'count',
                      t('page:supply48Data.csvDownloadDialog.countValue', {
                        count: csvExportRows.length,
                      }),
                    ],
                    [
                      'updatedAt',
                      csvUpdatedAt === undefined
                        ? '—'
                        : formatDateTimeFull(csvUpdatedAt),
                    ],
                  ] as const
                ).map(([key, value]) => (
                  <HStack key={key} gap={2}>
                    {/* 項目名は他のフォーム項目と同じ DS の form label
                        (textStyle="label" = md/semibold + high-emphasis) にする
                        (2026-08-26 デザイナー指摘)。 以前は sm/400 +
                        mid-emphasis の素の Text で、 ラベルと値が同じ字送り・
                        同じ太さになり「どちらが項目名か」 が読み取れなかった。
                        入力欄ではないので `FieldRoot` は使わず、 ラベルの見た目
                        だけを揃える (確定ダイアログ #6300 と同じ扱い)。
                        `width` は据え置き — 最長の「データ種別」 が md でも
                        88px に収まる。 */}
                    <Text
                      textStyle="label"
                      flexShrink={0}
                      width="88px"
                      color="Text.highEmphasis"
                    >
                      {t(`page:supply48Data.csvDownloadDialog.${key}`)}
                    </Text>
                    {/* 値も本文基準の md にする (sm のままだとラベルだけ大きく
                        なり行の重心がずれる)。 売買だけは DS `TradeLabel` の
                        pill で自前の寸法を持つため、 この指定は効かない
                        (= DS の見た目のまま。 意図どおり)。 */}
                    <Text fontSize="md">{value}</Text>
                  </HStack>
                ))}
              </VStack>
            </VStack>
          }
          confirmIcon={
            <FaArrowDownToBracketRegular boxSize={iconSize.labeledButton} />
          }
          confirmLabel={t('page:supply48Data.csvDownloadDialog.confirm')}
          isLoading={false}
          isOpen={isCsvDownloadOpen}
          onCancel={() => {
            setCsvDownloadOpen(false)
          }}
          onConfirm={handleCsvDownload}
          title={t('page:supply48Data.csvDownloadDialog.title')}
        />

        <ConfirmDialog
          body={t('page:supply48Data.pendingEditsGuard.body')}
          confirmLabel={t('page:supply48Data.pendingEditsGuard.confirm')}
          destructive
          isLoading={false}
          isOpen={showBlockerDialog}
          onCancel={() => {
            setShowBlockerDialog(false)
            if (blocker.state === 'blocked') {
              blocker.reset()
            }
          }}
          onConfirm={() => {
            setShowBlockerDialog(false)
            clearEditState()
            clearRejections()
            if (blocker.state === 'blocked') {
              blocker.proceed()
            }
          }}
          title={t('page:supply48Data.pendingEditsGuard.title')}
        />

        {/* §6 統合 ConfirmDialog */}
        {confirmDialogProps !== null && (
          <ConfirmDialog
            // AC-S4D-045: Save の確認メッセージは request 全体の違反を改行区切りで
            // 列挙しうる (複数行)。 whiteSpace="pre-line" で改行をそのまま可視化する
            // (他の kind は単一行なので影響なし)。
            body={
              <>
                <Text whiteSpace="pre-line">{confirmDialogProps.body}</Text>
                {pendingConfirm?.kind === 'overContract' && (
                  <OverContractCellList
                    cells={pendingConfirm.cells}
                    displayUnit={displayUnit}
                    itemLabelOf={(legId) =>
                      sourceDisplayLabel(
                        data?.sources.find((s) => s.supply48SourceId === legId),
                        t,
                      )
                    }
                  />
                )}
              </>
            }
            confirmLabel={confirmDialogProps.confirmLabel}
            destructive={confirmDialogProps.destructive}
            isLoading={
              pendingConfirm?.kind === 'overContract' ||
              pendingConfirm?.kind === 'inactiveKoma'
                ? isSaving
                : false
            }
            isOpen={pendingConfirm !== null}
            onCancel={() => setPendingConfirm(null)}
            onConfirm={() => {
              pendingConfirm?.run()
              setPendingConfirm(null)
            }}
            title={confirmDialogProps.title}
          />
        )}
      </Box>
    </WorkflowPageLayout>
  )
}
